import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { CheckoutCostCenterFacade } from '@spartacus/checkout/b2b/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CheckoutDeliveryAddressFacade } from '@spartacus/checkout/base/root';
import {
  Address, CmsService, ConverterService, GlobalMessageService, GlobalMessageType, OccEndpointsService, Page,
  RoutingService, StateWithProcess,
  TranslationService,
  UserCostCenterService, UserIdService, WindowRef
} from '@spartacus/core';
import { Card } from '@spartacus/storefront';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CustomCheckoutDeliveryAddressService } from 'src/app/spartacus/custom/core/src/checkout/facade/custom-checkout-delivery-address.service';
import { firstnameLastnameFormatter } from '../../../../../../custom/formatter/firstname-lastname-formatter';
import { ICON_TYPE } from '../../../../../../custom/icons/icon.model';
import { CustomPaymentTypeService } from '../../../../core/src/checkout/facade/custom-payment-type.service';
import { CustomUserAddressService } from '../../../../core/user/facade/profile/custom-user-address.service';
import { StateWithUserAddressState } from '../../../../core/user/store/reducers/user-addresses-zone.reducer';

export interface CardWithAddress {
  card: Card;
  address: Address;
}

@Component({
  selector: 'cx-shipping-address',
  templateUrl: './custom-shipping-address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomShippingAddressComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  iconTypes = ICON_TYPE;
  addressFormOpened = false;
  showEditAddressForm = false;
  editCard: string;
  forceLoader = false; // this helps with smoother steps transition
  selectedAddress: Address;
  doneAutoSelect = false;
  isAccountPayment = false;
  showAddAddressForm = false;
  isCheckoutPage = false;
  currentAddress: Address;
  isDefaultAddress = false;
  page$ = this.cmsService.getCurrentPage();
  userId: string;
  isLoadingAddress$ = new BehaviorSubject<boolean>(true);

  constructor(
    protected storeAddresses: Store<StateWithUserAddressState | StateWithProcess<void>>,
    protected userAddressService: CustomUserAddressService,
    protected checkoutDeliveryAddressService: CustomCheckoutDeliveryAddressService,
    protected activatedRoute: ActivatedRoute,
    protected translation: TranslationService,
    protected activeCartFacade: ActiveCartFacade,
    protected checkoutStepService: CheckoutStepService,
    protected checkoutDeliveryAddressFacade: CheckoutDeliveryAddressFacade,
    protected winRef: WindowRef,
    protected userIdService: UserIdService,
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected converter: ConverterService,
    protected paymentTypeService?: CustomPaymentTypeService,
    protected userCostCenterService?: UserCostCenterService,
    protected checkoutCostCenterFacade?: CheckoutCostCenterFacade,
    protected routingService?: RoutingService,
    protected cmsService?: CmsService,
    protected globalMessageService?: GlobalMessageService
  ) { }

  getUserID(): void {
    this.userIdService.takeUserId()
      .subscribe(userID => this.userId = userID)
      .unsubscribe();
  }

  get isGuestCheckout(): boolean {
    this.subscriptions.add(this.activeCartFacade.isGuestCart().subscribe((data) => {
      return data;
    }));
    return false;
  }

  get backBtnText(): string {
    return this.checkoutStepService.getBackBntText(this.activatedRoute);
  }

  get isLoading$(): Observable<boolean> {
    if (this.isCheckoutPage) {
      return this.userAddressService.getAddressesByZoneLoading();
    } else {
      return this.userAddressService.getAddressesLoading();
    }
  }


  get cards$(): Observable<CardWithAddress[]> {
    return combineLatest([
      this.getSupportedAddresses(),
      this.getSelectedAddress(),
      this.translation.translate('checkoutAddress.defaultShippingAddress'),
      this.translation.translate('checkoutAddress.shipToThisAddress'),
      this.translation.translate('addressCard.selected'),
    ]).pipe(
      take(1),
      tap(([addresses, selected]) => {
          if (addresses.length) {
            this.selectDefaultAddress(addresses, selected);
          }
        },
      ),
      map(([addresses, selected, textDefault, textShipTo, textSelected]) =>
        (addresses as any).map((address) => ({
          address,
          card: this.getCardContent(
            address,
            selected,
            textDefault,
            textShipTo,
            textSelected,
          ),
        })),
      ),
    );
  }

  isCheckout(page: Page): boolean {
    this.isCheckoutPage = !!page.label.includes('checkout');
    return this.isCheckoutPage;
  }

  editAddressSubmit(address: Address): void {
    this.showEditAddressForm = false;
    if (this.isCheckoutPage) {
      this.userAddressService.updateUserAddressByZone(this.currentAddress.id, address);
    } else {
      this.userAddressService.updateUserAddress(this.currentAddress.id, address);
    }
  }

  editAddressCancel(): void {
    this.showEditAddressForm = false;
  }

  getSelectedAddress(): Observable<Address | undefined> {
    return this.checkoutDeliveryAddressFacade.getDeliveryAddressState().pipe(
      filter((state) => !state.loading),
      map((state) => state.data),
      tap((address) => {
        if (
          address &&
          (this.selectedAddress === undefined ||
            this.selectedAddress.id !== address.id)
        ) {
          this.selectedAddress = address;
          if (this.forceLoader) {
            this.checkoutStepService.next(this.activatedRoute);
          }
        }
      }),
    );
  }


  getCardContentObservable(address: Address): Observable<Card> {
    return combineLatest([
      this.getSelectedAddress(),
      this.translation.translate('addressCard.setAsDefault'),

    ]).pipe(
      map(
        ([
           selected,
           setAsDefaultText,
         ]) => {

          return {
            textBold: firstnameLastnameFormatter(address),
            text: [
              address.line1,
              address.line2,
              address.town + ', ' + address.postalCode,
              address.country.name,
            ],
            actions: [{ name: 'send', event: 'send' }, { name: 'editar', event: 'edit' }],
            header: selected && selected.id === address.id ? setAsDefaultText : '',
          };
        },
      ),
    );
  }

  getSupportedAddresses(): Observable<Address[]> {
    if (this.isAccountPayment) {
      return this.checkoutCostCenterFacade.getCostCenterState().pipe(
        filter((state) => !state.loading),
        map((state) => state.data),
        distinctUntilChanged((prev, curr) => prev?.code === curr?.code),
        switchMap((costCenter) => {
          this.doneAutoSelect = false;
          return costCenter?.code
            ? this.userCostCenterService.getCostCenterAddresses(costCenter.code)
            : of([]);
        }),
      );
    }

    return this.isCheckoutPage ? this.userAddressService.getAddressesByZone() : this.userAddressService.getAddresses();
  }

  selectDefaultAddress(addresses: Address[], selected: Address): void {
    if (
      !this.doneAutoSelect &&
      addresses &&
      addresses.length &&
      (!selected || Object.keys(selected).length === 0)
    ) {
      if (this.isAccountPayment) {
        if (addresses.length === 1) {
          this.selectAddress(addresses[0]);
        }
      } else {
        selected = addresses.find((address) => address.defaultAddress);
        if (selected) {
          this.selectAddress(selected);
        }
      }
      this.doneAutoSelect = true;
    }
  }

  ngOnInit(): void {
    if (
      this.paymentTypeService &&
      this.userCostCenterService &&
      this.checkoutCostCenterFacade
    ) {
      this.subscriptions.add(
        this.paymentTypeService
          .isAccountPayment()
          .pipe(distinctUntilChanged())
          .subscribe((isAccount) => {
            this.isLoadingAddress$.next(false);
            return (this.isAccountPayment = isAccount)
          }),
      );
    }

    if (!this.isGuestCheckout && !this.isAccountPayment) {
      this.cmsService.getCurrentPage().pipe(tap((page) => {
        this.isCheckoutPage = !!page.label.includes('checkout');
      })).subscribe(() => {
        if (this.isCheckoutPage) {
          this.userAddressService.loadAddressesByZone();
        } else {
          this.userAddressService.loadAddresses();
        }
        this.isLoadingAddress$.next(false);
      }).unsubscribe();
    }

    this.subscriptions.add(
      this.getSupportedAddresses().subscribe()
    )

    this.subscriptions.add(
      this.cards$.subscribe(
        (data) => {
          var defaultAddress = false;
          data.forEach((item, index) => {
            if (item?.address?.defaultAddress) {
              defaultAddress = true;
              this.addAddressIfIsNull(item?.address);
            }
            if (index + 1 === data?.length && !defaultAddress) {
              this.customSetAddressAsDefault(data[0]?.address?.id, data[0]?.address)
              this.isLoadingAddress$.next(false);
            }
          });
        }
      )
    )
  }

  addAddressIfIsNull(address: Address) {
    this.checkoutDeliveryAddressFacade.setDeliveryAddress(address).subscribe(
      () => {
        this.isLoadingAddress$.next(false);
      }
    ).unsubscribe();
  }

  getCardContent(
    address: Address,
    selected?: any,
    textDefaultShippingAddress?: string,
    textShipToThisAddress?: string,
    textSelected?: string,
  ): Card {

    let region = '';
    if (address.region && address.region.isocode) {
      region = address.region.isocode + ', ';
    }

    return {
      title: address.defaultAddress ? textDefaultShippingAddress : '',
      textBold: firstnameLastnameFormatter(address),
      text: [
        address.line1,
        address.line2,
        address.town + ', ' + address.postalCode,
        address.country.name,
      ],
      actions: [{ name: textShipToThisAddress, event: 'send' }, { name: 'editar', event: 'edit' }],
      header: selected && selected.id === address.id ? textSelected : '',
    };
  }

  selectAddress(address: Address): void {
    this.checkoutDeliveryAddressService.setDeliveryAddress(address);
  }

  addAddress(address: Address): void {
    this.forceLoader = true;
    if (Boolean(address)) {
      this.subscriptions.add(this.checkoutDeliveryAddressService.createAndSetAddress(address).subscribe(() => {
        this.checkoutStepService.next(this.activatedRoute);
      }));
    } else {
      this.forceLoader = false;
      this.checkoutStepService.next(this.activatedRoute);
    }
  }

  showNewAddressForm(): void {
    this.addressFormOpened = true;
  }

  hideNewAddressForm(goPrevious: boolean = false): void {
    this.addressFormOpened = false;
    this.showEditAddressForm = false;
    if (goPrevious) {
      this.back();
    }
  }

  customSetAddressAsDefault(addressId: string, address: Address): void {
    this.getUserID();
    const customAddress = JSON.parse(JSON.stringify(address));
    customAddress.defaultAddress = true;
    if (this.isCheckoutPage) {
      this.checkoutDeliveryAddressFacade.setDeliveryAddress(customAddress);
      this.subscriptions.add(this.userAddressService.updateAddress(this.userId, addressId, customAddress).subscribe());
    } else {
      this.userAddressService.customSetAddressAsDefault(addressId, customAddress);
    }
    this.selectAddress(address);
  }

  back(): void {
    this.checkoutStepService.back(this.activatedRoute);
  }

  hideEditAddressForm(): void {
    this.showEditAddressForm = false;
  }

  deleteAddress(addressId: string): void {
    this.userAddressService.deleteUserAddress(addressId);
    this.checkoutDeliveryAddressService.clearCheckoutDeliveryDetails();
  }

  editAddressButtonHandle(address: Address): void {
    this.showEditAddressForm = true;
    this.currentAddress = address;
  }

  cancelCard(): void {
    this.editCard = null;
  }

  editAddressCheckoutSubmit(address: Address): void {

    if (address === undefined) {
      this.showEditAddressForm = false;
      this.checkoutDeliveryAddressService.customUpdateUserAddress(this.currentAddress.id, address);
      this.winRef.location.reload();

    } else if (address.region.isocode !== null) {
      this.showEditAddressForm = false;
      this.checkoutDeliveryAddressService.customUpdateUserAddress(this.currentAddress.id, address);
      this.winRef.location.reload();
    }
  }

  setEdit(addressId: string): void {
    if (this.editCard !== addressId) {
      this.editCard = addressId;
    } else {
      this.deleteAddress(addressId);
    }
  }

  next(): void {
    const shippingCards = document.querySelector('.is-default');
    if (shippingCards) {
      this.checkoutStepService.next(this.activatedRoute);
    } else {
      this.clickDisabled();
    }
  }

  clickDisabled(): void {
    this.globalMessageService.add(
      { key: 'checkoutAddress.checkDefaultAddress' },
      GlobalMessageType.MSG_TYPE_ERROR
    );
  }


  ngOnDestroy(): void {
    this.userAddressService.clearAddresses();
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
